<script>
import { usePrimeVue } from "primevue/config";
import { mapState, mapActions } from 'pinia'
import { useSeoMeta } from "@unhead/vue";
import i18n from "@/i18n";
import {errorsStore} from "@/store/errorsStore";
import {authStore} from "@/store/authStore";
import {bankIdStore} from "@/store/bankIdStore";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import {notificationStore} from "@/store/notificationStore";
import {successMessageStore} from "@/store/successMessageStore";
import {contentReviewStore} from "@/store/contentReviewStore";
import {maintenanceStore} from "@/store/maintenanceStore";
import {managerStore} from "@/store/managerStore";
import {userSettingStore} from "@/store/userSettingStore";
import {userStore} from "@/store/userStore";
import {appStore} from "@/store/appStore";
import BaseLoader from "@/components/BaseLoader.vue";
import BankIdModal from "@/components/BankId/BankIdModal.vue";
import BaseSuccessMessage from "@/components/BaseSuccessMessage.vue";
import PleaseVerifyUserModal from "@/components/Modals/PleaseVerifyUserModal.vue";
import BaseLoginModal from "@/components/BaseLoginModal.vue";
import SignUpModal from "@/components/Authorization/SignUpModal.vue";
import SuccessfulSignUpModal from "@/components/Authorization/SuccessfulSignUpModal.vue";
import ResetPasswordModal from "@/components/Authorization/ResetPasswordModal.vue";
import ChangeEmailModal from "@/components/Modals/ChangeEmailModal.vue";
import LeavePageConfirmModal from "@/components/Modals/LeavePageConfirmModal.vue";
import EmailExistModal from "@/components/Authorization/EmailExistModal.vue";
import MainSearchResultModal from "@/components/MainSearch/MainSearchResultModal.vue";
import ManagerCompanyInfoModal from "@/views/manager/manager-modals/ManagerCompanyInfoModal.vue";
import ManagerCompanyInfoMandatoryModal from "@/views/manager/manager-modals/ManagerCompanyInfoMandatoryModal.vue";
import BaseErrorModal from "@/components/Modals/BaseErrorModal.vue";
import TakeOverConfirmModal from "@/components/Modals/TakeOverConfirmModal.vue";
import ShareModal from "@/components/Modals/ShareModal.vue";

export default {
  name: 'App',
  components: {
    BaseLoader,
    BankIdModal,
    BaseSuccessMessage,
    ChangeEmailModal,
    ResetPasswordModal,
    SignUpModal,
    SuccessfulSignUpModal,
    BaseLoginModal,
    PleaseVerifyUserModal,
    LeavePageConfirmModal,
    EmailExistModal,
    MainSearchResultModal,
    ManagerCompanyInfoModal,
    ManagerCompanyInfoMandatoryModal,
    BaseErrorModal,
    TakeOverConfirmModal,
    ShareModal
  },
  computed: {
    ...mapState(appStore, [
      'hideHeaderMessageState',
      'appTitle',
      'appDescription',
      'appImage',
      'structuredData'
    ]),
    ...mapState(maintenanceStore, [
      'isResolveContentReviewBlocked'
    ]),
    ...mapState(baseLoaderStore, [
        'loaderState'
    ]),
    ...mapState(userStore, [
      'userHasApprovedEmail'
    ]),
    ...mapState(userSettingStore,[
      'userSettingsInfo'
    ]),
    ...mapState(authStore, [
      'userInfo',
      'permissionUserRole',
      'userActiveProfile',
      'userTimeZoneId',
      'managerProfileInfo',
      'userIsActiveManager',
      'showCookiePolicyMessage'
    ]),
    ...mapState(managerStore, ['managerHasCompanyInfo']),
    ...mapState(bankIdStore, ['bankIdModalState']),
    ...mapState(successMessageStore, ['successMessageState']),
    monthNames() {
      return [
        i18n.global.t('monthNames.january'),
        i18n.global.t('monthNames.february'),
        i18n.global.t('monthNames.march'),
        i18n.global.t('monthNames.april'),
        i18n.global.t('monthNames.may'),
        i18n.global.t('monthNames.june'),
        i18n.global.t('monthNames.july'),
        i18n.global.t('monthNames.august'),
        i18n.global.t('monthNames.september'),
        i18n.global.t('monthNames.october'),
        i18n.global.t('monthNames.november'),
        i18n.global.t('monthNames.december')
      ]
    },
    dayNamesMin() {
      return [
        i18n.global.t('dayNamesMin.su'),
        i18n.global.t('dayNamesMin.mo'),
        i18n.global.t('dayNamesMin.tu'),
        i18n.global.t('dayNamesMin.we'),
        i18n.global.t('dayNamesMin.th'),
        i18n.global.t('dayNamesMin.fr'),
        i18n.global.t('dayNamesMin.sa')
      ]
    },
    baseContentClass() {
      let contentClass = '';

      if (!this.userHasApprovedEmail) {
        contentClass += this.hideHeaderMessageState ? '' : ' not-approved-user-header-message';
      } else if (this.showCookiePolicyMessage) {
        contentClass += ' not-accepted-cookie-header-message';
      } else if (this.isResolveContentReviewBlocked) {
        contentClass += this.hideHeaderMessageState ? '' : ' content-review-blocked';
      }

      return this.$route.meta.isFrame ? contentClass : "";
    },
    title() {
      return this.appTitle ? this.appTitle : 'PeoplePolitics';
    },
    description() {
      return this.appDescription ? this.appDescription : 'Peoplepolitics is a social media platform that allows users to create and follow topics, organizations, and proposals, and provides notifications for critical events.';
    },
    image() {
      return this.appImage ? this.appImage : '/html/app/favicon.png';
    }
  },
  data() {
    return {
      appLoaded: false
    }
  },
  watch: {
    userActiveProfile(profile) {
      if (!profile) {
        return;
      }
      if (profile.type === 'MEMBER') {
        this.initMemberProfile();
      } else if (profile.type === 'MANAGER') {
        this.initManagerProfile();
      }
    },
    structuredData(data) {
      if (!data) {
        return;
      }
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    }
  },
  methods: {
    ...mapActions(errorsStore, [
      'addResponseInterceptors',
    ]),
    ...mapActions(userSettingStore,[
      'updateUserSettingAction'
    ]),
    ...mapActions(userSettingStore,[
      'getUserSettings'
    ]),
    ...mapActions(authStore, [
      'checkCookieAcceptedAction',
      'authGetUserData',
      'getMemberProfile',
      'getManagerProfile',
      'getApprovedOrganizations',
      'openSuccessfulSignUpModal'
    ]),
    ...mapActions(managerStore, [
      'getManagerProfileInfo',
      'openCompanyInfoMandatoryModalState',
      'closeCompanyInfoMandatoryModalState'
    ]),
    ...mapActions(notificationStore, [
      'getUnreadNewsCountAction',
      'getCriticalEventCountAction'
    ]),
    ...mapActions(baseLoaderStore, [
      'showBaseLoader',
      'hideBaseLoader'
    ]),
    ...mapActions(contentReviewStore, [
      'getCrItemProposers'
    ]),
    ...mapActions(maintenanceStore, [
        'getResolveContentReviewInfo',
        'initEmptyResolveContentReviewInfo'
    ]),

    initSeoMetadata() {
      useSeoMeta({
        title: () => this.title,
        ogTitle: () => this.title,
        description: () => this.description,
        ogDescription: () => this.description,
        ogImage: () => this.image
      });
    },

    initApp() {
      this.checkCookieAcceptedAction();
      this.addResponseInterceptors();

      this.authGetUserData().then(() => {
        this.getUserSettings().then(()=>{
          this.$i18n.locale = this.userSettingsInfo['LANGUAGE'];
        });

        if (this.permissionUserRole !== 'GUEST') {
          this.getApprovedOrganizations();
          this.getUnreadNewsCountAction();
          this.getCriticalEventCountAction();
        }

        if (localStorage.getItem("signUpCallback") === 'true') {
          this.openSuccessfulSignUpModal();
          localStorage.setItem("signUpCallback", 'false');
        }

        if (!this.userInfo) {
          localStorage.removeItem('companyInfoMandatoryState');
        }

        this.appLoaded = true;
      });
    },

    initMemberProfile() {
      console.log('initMemberProfile');

      if (this.$route.meta.managerProfile) {
        this.$router.push({ name: 'Index' });
      }
      this.getMemberProfile(this.userActiveProfile.profileId).then(()=> {});
    },

    initManagerProfile() {
      console.log('initManagerProfile');

      this.getManagerProfile().then(() => {
        if (this.userIsActiveManager) {
          this.getResolveContentReviewInfo();
          this.getManagerProfileInfo(this.managerProfileInfo.id).catch(()=> {
            if (localStorage.getItem('companyInfoMandatoryState') !== 'true') {
              this.openCompanyInfoMandatoryModalState();
              localStorage.setItem("companyInfoMandatoryState", "true");
            }
          });
        } else {
          this.initEmptyResolveContentReviewInfo();
        }
      });
    },

    checkMainAppRouter(url) {
      let hasView = this.$router.options.routes.filter(route => {
        return route.framePath === url;
      }).length > 0;

      if (hasView) {
        this.$router.push(url);
      }

      return hasView;
    },
    handleFrameNavigation() {
      window.addEventListener("message",  (e) => {
        const url = e.data.contentFrameUrl;
        const showLoader = e.data.showLoader;
        const hideLoader = e.data.hideLoader;
        const setFrameContentHeight = e.data.setFrameContentHeight;
        const mandatoryCompanyInformation = e.data.mandatoryCompanyInformation;
        const setSetting = e.data.setSetting;
        const openProposalView = e.data.openProposalView;

        if (url) {
          this.$router.push(url.replace('&_f=true','').replace('?_f=true',''));
        }

        if (showLoader) {
          this.showBaseLoader();
        }

        if (hideLoader) {
          this.hideBaseLoader();
        }

        if (setFrameContentHeight) {
          const frame = document.getElementById('managerViewFrame');

          if (frame) {
            const frameScrollHeight = document.getElementById('managerViewFrame').scrollHeight;
            this.viewClass = (setFrameContentHeight || 800) < frameScrollHeight  ? '' : 'manager-view-tabs--has-frame-scroll';
          }
        }

        if (mandatoryCompanyInformation) {
          this.openCompanyInfoMandatoryModalState({
            type: 'TASK'
          });
        }

        if (setSetting) {
          this.updateUserSettingAction(setSetting.type, setSetting.value);
        }

        if (openProposalView) {
          this.$router.push({ name: 'ProposalView', params: { id: openProposalView.proposalId }});
        }

      }, false);
    },
    setViewport() {
      const viewportWidth = 1024;

      if (screen.width < viewportWidth) {
        const mvp = document.getElementById('metaviewport');
        mvp.setAttribute('content','user-scalable=no,width=' + viewportWidth);
      }
    },
    showCrProposersModal(proposalIssueNumber) {
      this.showBaseLoader();
      this.getCrItemProposers(proposalIssueNumber).then(()=> {
        this.hideBaseLoader();
      });
    },
    processLocale() {
      const primevue = usePrimeVue();
      primevue.config.locale.firstDayOfWeek = 1;
      primevue.config.locale.monthNames = this.monthNames;
      primevue.config.locale.dayNamesMin = this.dayNamesMin;
    }
  },
  beforeMount() {
    this.initSeoMetadata();
    this.initApp();
    this.handleFrameNavigation();
    this.setViewport();
  },
  created () {
    //Set global config
    document.title = "PeoplePolitics";
    this.processLocale();
  }
}
</script>

<template>
  <link type="text/css" rel="stylesheet" href="https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700">
  <link rel="stylesheet" href="https://use.fontawesome.com/releases/v5.7.2/css/all.css" integrity="sha384-fnmOCqbTlWIlj8LyTjo7mOUStjsKC4pOpQbqyi7RrhN7udi9RwhKkMHpvLbHG9Sr" crossorigin="anonymous">
  <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">

  <div v-if="appLoaded"
       class="base-content-wrap"
       :class="baseContentClass"
  >
    <RouterView ref="routerView" :key="$route.path"/>
  </div>

  <base-success-message v-if="successMessageState"/>
  <base-loader :state="loaderState"/>

<!--  Modals -->
  <bank-id-modal v-if="bankIdModalState"/>
  <base-login-modal/>
  <sign-up-modal/>
  <successful-sign-up-Modal/>
  <reset-password-modal/>
  <change-email-modal/>
  <leave-page-confirm-modal/>
  <please-verify-user-modal/>
  <email-exist-modal/>
  <main-search-result-modal/>
  <manager-company-info-modal/>
  <manager-company-info-mandatory-modal/>
  <base-error-modal/>
  <take-over-confirm-modal/>
  <share-modal/>
</template>

<style lang="scss">
@import "~@/assets/scss/base/reset";
@import "~@/assets/scss/base/variables";
@import "~@/assets/scss/base/helpers";
@import "~@/assets/scss/base/transition";
@import "~@/assets/scss/TreeComponent.scss";
@import "~@/assets/scss/components/Button.scss";

  html {
    height: auto !important;
    font: 14px/18px 'Open Sans',Arial,sans-serif !important;
  }

  .base-content-wrap {
    margin: 0 auto 0;

    &.not-approved-user-header-message {
      padding-top: 45px;
    }

    &.not-accepted-cookie-header-message {
      padding-top: 80px;
    }

    &.content-review-blocked {
      padding-top: 45px;
    }
  }

  html,
  body,
  .base-content-wrap,
  #app {
    min-height: 100vh;
    position: relative;
    top: 0;
    left: 0;
  }

  body {
    position: relative;
    color: #3c3c3c;
  }

  //Tooltip
  .p-tooltip {
    border-radius: 2px;

    .p-tooltip-text {
      padding: 7px;
      color: #ffffff;
      background: #4f4f4f;
      font-size: 12px;
    }
    .p-tooltip-arrow {
      border-top-color: #4f4f4f !important;
      border-bottom-color: #4f4f4f !important;
    }
  }

  // Base dialog
  .p-dialog {
    border-radius: 5px !important;

    .p-dialog-header {
      display: flex;
      align-items: center;
      height: 50px;
      padding: 0 15px;
      background: #f2f2f2;
      border-radius: 5px 5px 0 0;

      .p-dialog-title {
        font-size: 16px;
        font-weight: 600;
        color: #7835a6;
      }

      .p-dialog-close-button {
        color: #7835a6;
        &:hover,
        &:focus-visible{
          border: transparent;
          color: #7835a6 !important;
          background: transparent !important;
          outline: none;
        }
      }
    }

    .p-dialog-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      height: 50px;
      padding: 0 15px;
      background: #f2f2f2;
      border-radius: 0 0 5px 5px;

      .p-dialog-primary-btn {
        height: 32px;
        padding: 0 15px;
        background: #7130a0;
        font-family: 'Open Sans',Arial,sans-serif;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        color: #fff;
        border-radius: 4px;
        border: none;
        outline: none;
        cursor: pointer;
        &:hover {
          background: #672992;
        }

        &.disabled {
          background: #909090;
          cursor: default;
        }
      }

      .p-dialog-close-btn {
        font-family: 'Open Sans',Arial,sans-serif;
        font-size: 14px;
        font-weight: 600;
        border: none;
        outline: 0;
        text-transform: uppercase;
        cursor: pointer;
        color: #7130a0;
        background: transparent;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

.p-toast-message-success {
  background: #F1FDF5;
}
</style>
